<script setup lang="ts">
import { computed } from 'vue';
import {
    DisabledPropsInterface,
    useDisableComposable,
} from '@src/application/composables/disabled-composable';
import Icon from '@components/icons/icon.vue';
import Button from '@components/buttons/button.vue';
import { TooltipProps } from '@components/tooltip-props.ts';
import { IconType } from '@components/icons/icon-types.ts';

const {
    icon,
    context = 'default',
} = defineProps<DisabledPropsInterface & {
    icon: IconType,
    context?: 'default'|'primary'|'danger'|'primary-solid'|'danger-solid',
    tooltip?: TooltipProps,
    spread?: boolean,
    grow?: boolean,
}>();

const { disabled } = useDisableComposable();

const iconColor = computed(() => {
    if (disabled.value) {
        return 'inherit';
    }
    if (context === 'default' || context === 'primary') {
        return context;
    }
    if (context === 'danger') {
        return 'red';
    }
    return 'white';
});
</script>

<template>
    <Button
        :class="[
            $style.button_square,
            $style.icon_button,
            $style[context],
            disabled ? $style.disabled : '',
        ]"
        :disabled="disabled"
        :tooltip="tooltip"
        :spread="spread"
        :grow="grow"
    >
        <Icon :icon="icon" :class="$style.icon" :icon-color="iconColor"/>
    </Button>
</template>

<style lang="scss" module>
 @use '@text' as *;
 @use '@borders' as *;
 @use '@backgrounds' as *;

.button_square.icon_button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2.2em;
    min-height: 2.2em;
    padding: initial;

    @media (min-width: 38em) {
        min-width: 2.5em;
        min-height: 2.5em;
    }
    .icon {
        font-size: 1.1em;
        cursor: inherit;
    }
    &:not(.disabled) {
        &:hover:not(.default) .icon { color: #fff }
        &.primary,
        &.primary button {
            border-color: $primary-line-color;

            &:hover {
                background-color: $primary-background;
                border-color: $primary-line-color;
            }
        }
        &.danger,
        &.danger button {
            border-color: $red-line-color;

            &:hover {
                background-color: $red-background;
                border-color: $red-background;
            }
        }
        &.primary-solid,
        &.primary-solid button {
            background-color: $primary-background;
            border-color: $primary-line-color;

            &:hover {
                background-color: $primary-background-hover;
                border-color: $primary-background-hover;
            }
        }
        &.danger-solid,
        &.danger-solid button {
            background-color: $red-background;
            border-color: $red-background;

            &:hover {
                background-color: $red-background-hover;
                border-color: $red-background-hover;
            }
        }
    }
}
</style>
